import React, {useContext, useEffect, useMemo, useState} from "react";
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid2';
import TransactionInput from "../Transactions/TransactionInput/TransactionInput";
import {styled} from '@mui/material/styles'
import formatters from "../../utils/formatter";
import {appConfig, getTokenHeader} from "../../utils/settings";
import {Box, Fab, Tooltip as MUITooltip} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {AccountContext} from "../../context/accountContext";
import {UserContext} from "../../context/userContext";
import Typography from "@mui/material/Typography";
import DateYearSelection from "./DateYearSelection";
import AppDrawer from "../AppDrawer/AppDrawer";
import {LineChart} from "@mui/x-charts/LineChart";
import {AuthContext} from "../../context/authContext";
import BudgetStatus from "../shared/BudgetStatus";
import {PieChart} from "@mui/x-charts";
import TransactionsByUser from "../shared/TransactionsByUser";
import CategorySpendBarChart from "../shared/CategorySpendBarChart";

const PREFIX = 'Dashboard';
const classes = {
    root: `${PREFIX}-root`,
    paper: `${PREFIX}-paper`,
    lineGraph: `${PREFIX}-lineGraph`,
    floatingAddBtn: `${PREFIX}-floatingAddBtn`
}
const Root = styled('div')(({ theme }) => ({
    [`&.${classes.root}`]: {
       display: "flex",
       paddingTop: '64px'
    },
    [`& .${classes.paper}`]: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
       backgroundColor: '#f5f5f5',
        width: '100%'
    },
    [`& .${classes.lineGraph}`]: {
        minHeight: "200px"
    },
   [`& .${classes.floatingAddBtn}`]: {
      padding: theme.spacing(2),
      display: 'flex',
      justifyContent: 'flex-end',
   },
}))


export default function Dashboard(props) {
  const {setPasswordExpired} = useContext(AuthContext);
  const {user, userLoading, selectedMonth, selectedYear, setSelectedMonth, setSelectedYear} = useContext(UserContext);
  const {accountCategories, accountFamily, loadingAccountCategories} = useContext(AccountContext);
  const [budgets, setBudgets] = React.useState([]);
  const [budgetsLoading, setBudgetsLoading] = React.useState(false);
  const [transactionDataLoading, setTransactionDataLoading] = useState(true);
  const [transactionData, setTransactionData] = useState([]);
  const [spendAcrossCategoriesDataset, setSpendAcrossCategoriesDataset] = useState([]);

  const [dataset, setDataset] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});

  // Transaction Input Modal
   const [openTransactionInputModal, setOpenTransactionInputModal] = React.useState(false);
   const handleOpenTransactionInputModal = () => setOpenTransactionInputModal(true);
   const handleCloseTransactionInputModal = () => setOpenTransactionInputModal(false);

   const handleMonthChange = e => { setSelectedMonth(e.target.value)}
   const handleYearChange = e => setSelectedYear(e.target.value);



   // Custom settings for line graph
   const customize = {
      height: 450,
      legend: { hidden: true },
      margin: { left: 65, right: 30, top: 25, bottom: 30 },
   };

  // Main total and per category line graph
   useEffect(() => {
      if(!transactionDataLoading && !budgetsLoading) {
         setDataset(formatters.generateTransactionDataset(transactionData, budgets));
      }
   }, [transactionData, accountCategories, budgets]);

  // A handy map of categories
   useEffect(() => {
      const map = accountCategories.reduce((acc, category) => {
         acc[category.id] = category;
         return acc;
      }, {});
      setCategoryMap(map);
   }, [accountCategories]);

   useEffect(() => {
      setTransactionDataLoading(false);
   }, [transactionData]);

  // Transaction data async load
   useEffect(() => {
      if(!userLoading) {
         (async () => {
            const options = {method: "GET", headers: getTokenHeader()};
            const response = await fetch(appConfig.host +'/api/v1/transactions/' + selectedMonth + '/' + selectedYear, options);
            // Exit and require login
            if(response.status === 401) {
               setPasswordExpired(true);
            }
            const transactions = await response.json();
            setTransactionData(transactions);
         })();
      }
  }, [selectedMonth, selectedYear, userLoading, transactionDataLoading]);

   // Category Budgets async load to populate total budget for graphs
   useEffect(() => {
      if(!userLoading) {
         (async () => {
            const options = {method: "GET", headers: getTokenHeader()};
            const response = await fetch(appConfig.host +'/api/v1/category/budgets', options);
            // Exit and require login
            if(response.status === 401) {
               setPasswordExpired(true);
            }
            const budgets = await response.json();
            setBudgets(budgets);
            setBudgetsLoading(false);
         })();
      }
   }, [budgetsLoading, loadingAccountCategories, userLoading]);

   useEffect(() => {
      if(accountCategories.length > 0 && budgets.length > 0) {
        const chartData = generateDonutChartData(transactionData, accountCategories, calculateBudgetSum(budgets), categoryMap);
        setSpendAcrossCategoriesDataset(chartData);
      }
   }, [transactionData, accountCategories, budgets]);


   const dataForCategoryBarCharts = useMemo(() => {
      // Return empty array if essential data isn't ready
      if(accountCategories.length > 0 && budgets.length > 0) {
         return createCategoryBarChartDatasets(transactionData, accountCategories, budgets);
      } else return [];


   }, [budgets, transactionData, accountCategories]);

   function calculateBudgetSum(budgets){
      const sum = budgets.reduce((accumulator, currentValue) => {
         return accumulator + currentValue.categoryBudget
      }, 0)
      return sum;
   }

   // All transactions that are not of category other
  function calculateCategorizedTransactionsSum(transactionData) {
     const otherCategory = "8b7df87a-4ea3-4511-b84e-6b7005cfb773";
     const sum = transactionData.filter(t => t.category !== otherCategory).reduce((accumulator, currentValue) => {
       return accumulator + currentValue.amount
     }, 0)
    return sum;
  }

   // All transactions that are not of category other
   function calculateOtherTransactionsSum(transactionData) {
      const otherCategory = "8b7df87a-4ea3-4511-b84e-6b7005cfb773";
      const sum = transactionData.filter(t => t.category === otherCategory).reduce((accumulator, currentValue) => {
         return accumulator + currentValue.amount
      }, 0)
      return sum;
   }

   function getTotalSpendForCategory(transactions, categoryId) {
      const filteredTransactions = transactions.filter(transaction => {
         return transaction.category === categoryId;
      });

      // Calculate total spend for the selected category
      const totalSpend = filteredTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);
      return totalSpend;
   }

   function createCategoryBarChartDatasets(transactions, categories, categoryBudgets) {
      const datasets = [];

      // Iterate through each category budget
      categoryBudgets.forEach((budget) => {
         const categoryId = budget.categoryId;
         const budgetAmount = budget.categoryBudget;
         const fullCategory = categories.find(category => category.id === categoryId);
         const categoryName = fullCategory.category;
         const categorySpend = getTotalSpendForCategory(transactions, categoryId);

         const categoryBarChartData = {
            categoryId: categoryId,
            categoryName: categoryName,
            totalSpend: categorySpend,
            categoryBudget: budgetAmount
         }

         datasets.push(categoryBarChartData);
      });
      return datasets;
   }


   return (
      <Root className={classes.root}>
        <AppDrawer/>
         <Paper className={classes.paper}>
            {/*Transaction Input Modal*/}
            <TransactionInput open={openTransactionInputModal} accountId={user?.accountId} serverUrl={appConfig.host} categories={accountCategories} handleClose={handleCloseTransactionInputModal} saveHandler={setTransactionDataLoading}/>

             <Grid container alignItems="center" sx={{paddingBottom: "16px"}}>
                <Grid size={{ lg: 6, md: 6, sm: 12}}>
                   <DateYearSelection month={selectedMonth} year={selectedYear} handleMonthChange={handleMonthChange} handleYearChange={handleYearChange}/>
                </Grid>

                <Grid size={6} className={classes.floatingAddBtn}>
                   <MUITooltip title={"Add Transaction"}>
                      <Fab color="secondary" onClick={handleOpenTransactionInputModal}  aria-label="add">
                         <AddIcon />
                      </Fab>
                   </MUITooltip>
                </Grid>
             </Grid>

            <Grid container spacing={2}>
               <Grid container size={{xs: 12, lg: 8}}>
                  {/*Budget Overview*/}
                  <Grid size={{xs: 12, sm: 8}}>
                     <BudgetStatus totalBudget={calculateBudgetSum(budgets)} categorizedSpend={calculateCategorizedTransactionsSum(transactionData)} otherSpend={calculateOtherTransactionsSum(transactionData)}></BudgetStatus>
                  </Grid>

                  <Grid size={{xs:12, sm: 4}}>
                     <TransactionsByUser transactions={transactionData} accountUsers={accountFamily}></TransactionsByUser>
                  </Grid>

                  {/*Line graph of budget over time*/}
                  <Grid size={{ xs: 12}}>
                     <Paper className={classes.lineGraph} elevation={3}
                            sx={{
                               flexGrow: 1,
                               p: 2, // Padding inside Paper
                               display: 'flex',
                               flexDirection: 'column', // Stack the line items vertically
                               gap: 1.5 // Add some vertical space between lines
                            }}>
                        {transactionDataLoading ? null: (
                           <Box sx={{alignItems: 'center', textAlign: 'center'}}>
                              <Typography variant={'h5'}>Category Budgets timeline</Typography>
                              <LineChart
                                 xAxis={[{
                                    scaleType: 'time',
                                    dataKey: 'date',
                                    valueFormatter: (value) => {
                                       return new Date(value).toLocaleDateString('en-US', {
                                          month: '2-digit',
                                          day: '2-digit',
                                       });
                                    },
                                 }]}
                                 yAxis={[{ valueFormatter: formatters.formatCurrency }]}
                                 series={[
                                    ...Object.keys(dataset[0] || {})
                                       .filter((key) => key !== 'date' && key !== 'total')
                                       .map((categoryId) => ({
                                          dataKey: categoryId,
                                          label: categoryMap[categoryId].category || categoryId,
                                          showMark: false,
                                          color: categoryMap[categoryId].color || formatters.getRandomChartColor(),
                                       })),
                                    {
                                       dataKey: 'total',
                                       label: 'Total Remaining Budget',
                                       showMark: true,
                                       color: '#4db6ac'
                                    },
                                 ]}
                                 dataset={dataset}
                                 {...customize}
                              />
                           </Box>

                        )}
                     </Paper>
                  </Grid>


                  {/*Across category Donut Chart*/}
                  <Grid size={{xs: 12}}>
                     <Paper elevation={3} sx={{
                        minHeight: "480px",
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 2,
                        gap: 1.5
                     }}>
                        <Typography variant="h5" component="div" sx={{ mb: 1, mt: 1, textAlign: 'center'}}>
                           Spend Across Categories
                        </Typography>
                        <PieChart
                           series={[
                              {
                                 innerRadius: 40,
                                 data: spendAcrossCategoriesDataset,
                                 cx: 220,
                                 cy: 200,
                              },
                           ]}
                           width={600}
                           height={400}
                           slotProps={{
                              legend: {
                                 labelStyle: { fontSize: '14px'},
                                 position: { vertical: 'top', horizontal: 'right' },
                                 anchorOrigin: { vertical: 'top', horizontal: 'right' },
                                 sx: {
                                    marginRight: '20px',
                                    marginTop: '20px',
                                 },
                              },
                           }}
                        />
                     </Paper>
                  </Grid>
               </Grid>

               {/*Graph per category*/}
               <Grid container size={{xs: 12, lg: 4}}>
                  <Paper>
                     <Grid><Typography sx={{ textAlign: 'center', marginTop: '16px' }} gutterBottom variant="h5">Category Budgets Overview</Typography></Grid>
                     {/*Graphs go here*/}
                     <Grid container spacing={2}>
                        {dataForCategoryBarCharts.length > 0 ? (
                           dataForCategoryBarCharts.map((categoryData) => (
                              <Grid size={{xs:12, sm: 6}} key={categoryData.categoryId}>
                                 <CategorySpendBarChart
                                    categoryId={categoryData.categoryId}
                                    categoryName={categoryData.categoryName}
                                    totalSpend={categoryData.totalSpend}
                                    categoryBudget={categoryData.categoryBudget}
                                 />
                              </Grid>
                           ))
                        ) : (
                           <Grid>
                              <Typography>No budget data available.</Typography>
                           </Grid>
                        )}
                     </Grid>
                  </Paper>
               </Grid>
            </Grid>


            <div style={{ padding: 15}}></div>
         </Paper>
      </Root>
    );

  function generateDonutChartData(transactions, accountCategories, totalBudget, categoryMap) {
    // Calculate total spend per category
    const categoryTotals = {};

    if(transactions.length > 0) {
       transactions.forEach(transaction => {
          const categoryId = transaction.category;
          const transactionAmount = transaction.amount;

          if (categoryTotals[categoryId] === undefined) {
             categoryTotals[categoryId] = 0;
          }

          categoryTotals[categoryId] += transactionAmount;
       });
    }

    // Prepare data for mui x pie chart
    const chartData = []

    // Handle no spend for month case
    if(Object.keys(categoryTotals).length === 0) {
       chartData.push(
          {
             id: 0, value: totalBudget,
             label: "You've spent nothing!",
             color: formatters.getRandomChartColor()
          });
    } else {
       // Populate chartData with category names, spending, and color
       Object.keys(categoryTotals).forEach(categoryId => {
          const categoryName = accountCategories.find(category => category.id === categoryId).category;
          const spending = categoryTotals[categoryId];
          chartData.push(
             {
                id: categoryId,
                value: spending,
                label: categoryName,
                color: categoryMap[categoryId].color || formatters.getRandomChartColor()
             });
       });
    }
    return chartData;
  }
}

